import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import { ChevronRight, ArrowLeft, RefreshCw, Share2, Printer } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function PlacePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/public/place/${id}`),
      api.get(`/public/place/${id}/timeline`),
    ]).then(([a, b]) => { setData(a.data); setTimeline(b.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [id]);

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: data?.location?.name, url });
    else { navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
  };

  const candidates = data?.candidates || [];
  const totals = data?.totals || {};
  const ranked = useMemo(() => [...candidates].sort((a, b) => (totals[b.id]||0) - (totals[a.id]||0)), [candidates, totals]);
  const totalVotes = ranked.reduce((s, c) => s + (totals[c.id]||0), 0);
  const leader = ranked[0];

  if (loading && !data) return <div className="min-h-screen bg-[#FAFAFA] text-zinc-500 flex items-center justify-center text-xs uppercase tracking-[0.3em]">Loading...</div>;
  if (!data?.location) return <div className="min-h-screen bg-[#FAFAFA] text-zinc-900 flex items-center justify-center">Location not found. <Link to="/" className="ml-2 underline">Go home</Link></div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-900">
      <header className="border-b border-black/5 bg-white/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center gap-4 flex-wrap">
          <Link to="/" className="inline-flex items-center">
            <BrandMark variant="lockup" size={44}/>
          </Link>
          <div className="flex-1"/>
          <Link to="/" data-testid="back-home" className="text-xs uppercase tracking-[0.2em] flex items-center gap-2 border border-black/15 px-3 py-2 hover:bg-black/[0.06]">
            <ArrowLeft size={13}/> National
          </Link>
          <button onClick={shareLink} data-testid="share-btn" className="text-xs uppercase tracking-[0.2em] flex items-center gap-2 border border-black/15 px-3 py-2 hover:bg-black hover:text-white">
            <Share2 size={13}/> Share
          </button>
          <button onClick={load} className="p-2 border border-black/15 hover:bg-black/[0.06]" data-testid="refresh-place"><RefreshCw size={13}/></button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 pb-12">
        {/* breadcrumbs */}
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2 flex-wrap" data-testid="breadcrumbs">
          {(data.trail || []).map((t, i) => (
            <React.Fragment key={t.id}>
              {i > 0 && <ChevronRight size={12} className="text-zinc-700"/>}
              {t.id === id ? <span className="text-zinc-900">{t.name}</span> : <Link to={`/place/${t.id}`} className="hover:text-zinc-900">{t.name}</Link>}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-3 text-[11px] uppercase tracking-[0.3em] text-emerald-400">{data.location.level.replace("_"," ")}{data.location.code ? ` · ${data.location.code}` : ""}</div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-2 leading-[0.9]" data-testid="place-name">{data.location.name}</h1>
        <p className="text-zinc-400 mt-3 text-sm">{data.election?.name || "No active election"}</p>

        {/* leader */}
        {leader && (
          <div className="mt-8 border border-black/5 bg-white p-8 lg:p-10" style={{ boxShadow: `0 0 0 1px ${leader.color}30, 0 0 48px -8px ${leader.color}40` }} data-testid="place-leader">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Leading here</div>
            <div className="mt-3 flex items-start gap-4">
              {leader.photo_url ? (
                <img src={leader.photo_url} alt={leader.name} className="w-24 h-24 object-cover border-4 shrink-0" style={{ borderColor: leader.color }}/>
              ) : (
                <div className="w-2 h-24 rounded" style={{ background: leader.color }}/>
              )}
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] flex items-center gap-2" style={{ color: leader.color }}>
                  {leader.party_symbol && (leader.party_symbol.startsWith("http") ? <img src={leader.party_symbol} alt="" className="w-5 h-5"/> : <span className="text-lg">{leader.party_symbol}</span>)}
                  <span>{leader.party} · {leader.party_code}</span>
                </div>
                <div className="font-display text-4xl lg:text-6xl mt-1">{leader.name}</div>
                <div className="mt-4 flex items-end gap-8 flex-wrap">
                  <div><div className="text-xs text-zinc-500 uppercase tracking-wider">Votes</div><div className="font-display text-5xl tabular">{(totals[leader.id]||0).toLocaleString()}</div></div>
                  <div className="pb-2"><div className="text-xs text-zinc-500 uppercase tracking-wider">Share</div><div className="font-display text-3xl tabular">{totalVotes ? ((totals[leader.id]||0)/totalVotes*100).toFixed(1) : "0.0"}%</div></div>
                  <div className="pb-2"><div className="text-xs text-zinc-500 uppercase tracking-wider">Reporting</div><div className="font-display text-3xl tabular">{data.meta?.pu || 0} PU</div></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* standings */}
        <div className="mt-8 border border-black/5 bg-white">
          <div className="px-6 py-4 border-b border-black/5 text-xs uppercase tracking-[0.3em] text-zinc-500">Standings — {data.location.name}</div>
          <div className="divide-y divide-black/5">
            {ranked.map((c, i) => {
              const v = totals[c.id] || 0;
              const pct = totalVotes ? (v/totalVotes*100) : 0;
              return (
                <div key={c.id} className="px-6 py-4 flex items-center gap-4">
                  <div className="font-display text-2xl tabular text-zinc-500 w-8">{String(i+1).padStart(2,"0")}</div>
                  {c.photo_url ? (
                    <img src={c.photo_url} alt={c.name} className="w-11 h-11 object-cover border-2 shrink-0" style={{ borderColor: c.color }}/>
                  ) : (
                    <div className="w-1.5 h-10" style={{ background: c.color }}/>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                      {c.party_symbol && (c.party_symbol.startsWith("http") ? <img src={c.party_symbol} alt="" className="w-3.5 h-3.5"/> : <span>{c.party_symbol}</span>)}
                      <span>{c.party} · {c.party_code}</span>
                    </div>
                  </div>
                  <div className="hidden sm:block flex-1"><div className="h-1.5 bg-black/[0.04]"><div className="h-full transition-[width] duration-700" style={{ width: `${pct}%`, background: c.color }}/></div></div>
                  <div className="text-right w-32">
                    <div className="font-display text-xl tabular">{v.toLocaleString()}</div>
                    <div className="text-xs text-zinc-500 tabular">{pct.toFixed(1)}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* children */}
        {data.children?.length > 0 && (
          <div className="mt-8 border border-black/5 bg-white">
            <div className="px-6 py-4 border-b border-black/5 text-xs uppercase tracking-[0.3em] text-zinc-500">Drill down · {data.children.length} sub-areas</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {data.children.map(ch => {
                const rt = ranked.map(c => ({ c, v: (ch.totals[c.id]||0) }));
                const win = rt.sort((a,b)=>b.v-a.v)[0];
                const chTotal = rt.reduce((s,x)=>s+x.v,0);
                return (
                  <Link key={ch.location.id} to={`/place/${ch.location.id}`} data-testid={`child-${ch.location.id}`}
                    className="p-5 border-r border-b border-black/5 hover:bg-black/[0.03] transition-colors">
                    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{ch.location.name}</div>
                    {win && win.v > 0 ? (
                      <>
                        <div className="mt-2 flex items-center gap-2">
                          <span className="w-1 h-8" style={{ background: win.c.color }}/>
                          <div>
                            <div className="font-display text-lg leading-none">{win.c.name}</div>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{win.c.party_code}</div>
                          </div>
                        </div>
                        <div className="mt-3 flex items-end justify-between">
                          <div className="font-display text-2xl tabular">{win.v.toLocaleString()}</div>
                          <div className="text-xs text-zinc-500 tabular">{chTotal ? (win.v/chTotal*100).toFixed(1) : "0.0"}% · {ch.polling_units_reported} PU</div>
                        </div>
                      </>
                    ) : (
                      <div className="mt-4 text-xs text-zinc-600">No results yet</div>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        {/* Trend sparkline */}
        {timeline?.points?.length > 1 && (
          <div className="mt-8 border border-black/5 bg-white p-5" data-testid="trend-chart">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Vote share as results come in</div>
              <div className="text-xs text-zinc-500 tabular">{timeline.points.length} approvals</div>
            </div>
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <LineChart data={timeline.points.map((p, i) => ({ pu: p.pu, ...Object.fromEntries((timeline.candidates||[]).map(c => [c.party_code, +(p.share[c.id]||0).toFixed(1)])) }))}>
                  <XAxis dataKey="pu" stroke="#71717a" tick={{ fill: "#a1a1aa", fontSize: 11 }} axisLine={{ stroke: "#27272a" }} tickLine={false} label={{ value: "PUs reporting", position: "insideBottom", offset: -2, fill: "#71717a", fontSize: 10 }}/>
                  <YAxis unit="%" stroke="#71717a" tick={{ fill: "#a1a1aa", fontSize: 11 }} axisLine={{ stroke: "#27272a" }} tickLine={false} domain={[0, 100]}/>
                  <Tooltip contentStyle={{ background: "#18181B", border: "1px solid #27272A" }} labelStyle={{ color: "#fff" }} formatter={(v, n) => [`${v}%`, n]} labelFormatter={(v) => `PU #${v}`}/>
                  <Legend iconType="plainline" wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }}/>
                  {(timeline.candidates || []).map(c => (
                    <Line key={c.id} type="monotone" dataKey={c.party_code} stroke={c.color} strokeWidth={2.5} dot={false} isAnimationActive={false}/>
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* QR sticker (polling units only) */}
        {data.location.level === "polling_unit" && (
          <div className="mt-8 border border-black/5 bg-white p-6 print:bg-white print:text-black" data-testid="qr-sticker">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="p-4 bg-white">
                <QRCodeSVG value={typeof window !== "undefined" ? window.location.href : ""} size={168} bgColor="#ffffff" fgColor="#0A0A0A" level="M"/>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 print:text-zinc-700">Printable Sticker</div>
                <h3 className="font-display text-2xl mt-1">{data.location.name}</h3>
                <div className="text-xs uppercase tracking-wider text-emerald-400 mt-1">{data.location.code}</div>
                <p className="text-xs text-zinc-400 mt-3 max-w-md">Scan for real-time results from this polling unit. Print + laminate this sticker for your ward office, observer wall or campaign HQ.</p>
                <button onClick={() => window.print()} data-testid="print-sticker-btn"
                  className="mt-4 px-4 py-2 border border-black/15 text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white print:hidden flex items-center gap-2">
                  <Printer size={13}/> Print sticker
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
